// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo, useState } from 'react';
import type {
  PhaseBand,
  PlanChartSeries,
  SeriesInterpolation,
  SeriesVariant,
} from '../../utils/planSeries';
import './PlanTimeSeriesChart.css';

/**
 * Compact read-only line chart for plan curves — the narration's Drivers and
 * Actions tabs and the Live view.
 *
 * Hand-rolled rather than @carbon/charts because the defining visual is the
 * shaded phase bands behind the curves plus a persistent NOW cursor, and Carbon
 * charts (1.27) supports threshold *lines* only, not shaded x-ranges. The rest
 * of the surrounding page chrome is Carbon.
 *
 * Laid out as an HTML y-axis column beside a bordered plot: the curves live in
 * a `0 0 100 100` viewBox stretched to the plot with non-scaling strokes, while
 * the axis labels stay upright HTML text at any card width.
 */

const Y_TARGET_TICKS = 6;
const X_GRID_DIVISIONS = 8;

/** Dot/tooltip colour for the baseline curve, which draws as a muted dash. */
const BASELINE_HOVER_COLOR = '#8d8d8d';

/** Live (measured) curves read green — the same token as the header's Live pill. */
const LIVE_COLOR = 'var(--st-ok)';

const DEFAULT_COLORS: Partial<Record<SeriesVariant, string>> = { live: LIVE_COLOR };

/** Palette for phase bands, cycled by phase order — pre-cool, peak, then rest. */
const BAND_COLORS = ['#be95ff', '#ff832b', '#4589ff', '#42be65', '#f1c21b'];

export function phaseBandColor(index: number): string {
  return BAND_COLORS[index % BAND_COLORS.length];
}

interface Scale {
  min: number;
  max: number;
  ticks: number[];
  decimals: number;
}

/**
 * A rounded axis whose ticks land on human numbers (0.05, 50, 500) so the
 * gridlines and the curve read against the same clean scale. Falls back to a
 * unit-wide domain for a flat or non-finite series rather than dividing by zero.
 */
function niceScale(rawMin: number, rawMax: number): Scale {
  let min = rawMin;
  let max = rawMax;
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1, ticks: [0, 1], decimals: 0 };
  }
  if (min === max) {
    const pad = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
    min -= pad;
    max += pad;
  }
  const rawStep = (max - min) / (Y_TARGET_TICKS - 1);
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const norm = rawStep / mag;
  const stepNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  const step = stepNorm * mag;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const decimals = step < 1 ? Math.min(4, Math.ceil(-Math.log10(step))) : 0;
  const ticks: number[] = [];
  // The `step / 1000` guard keeps float drift from appending a stray tick.
  for (let v = niceMin; v <= niceMax + step / 1000; v += step) {
    ticks.push(Number(v.toFixed(decimals + 2)));
  }
  return { min: niceMin, max: niceMax, ticks, decimals };
}

function isDrawable(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}

/**
 * One path per unbroken run of values. Nulls end the current run instead of
 * being interpolated across: a Live series that stops at "now" must not draw a
 * straight line to the right edge, which would read as a prediction the plan
 * never made.
 *
 * `step` joins samples with a hold-then-jump (`H` then `V`) instead of a
 * diagonal, for discrete quantities — see {@link PlanChartSeries.interpolation}.
 */
function subpaths(
  values: (number | null)[],
  xAt: (_index: number) => number,
  yAt: (_value: number) => number,
  interpolation: SeriesInterpolation = 'linear'
): string[] {
  const paths: string[] = [];
  const stepped = interpolation === 'step';
  let current: string[] = [];
  let lastIndex = -1;

  const flush = () => {
    // A stepped sample holds until the next one, so the last sample of a run
    // extends across its own bucket rather than stopping dead at its own x.
    // That also gives a lone stepped sample visible width.
    if (stepped && current.length > 0) current.push(`H${xAt(lastIndex + 1).toFixed(2)}`);
    // A lone point has no line to draw, but should still show as a dot-sized
    // mark, so it's emitted as a zero-length segment.
    if (current.length === 1) paths.push(`${current[0]} ${current[0].replace(/^M/, 'L')}`);
    else if (current.length > 1) paths.push(current.join(' '));
    current = [];
    lastIndex = -1;
  };

  values.forEach((value, i) => {
    if (!isDrawable(value)) {
      flush();
      return;
    }
    if (current.length === 0) {
      current.push(`M${xAt(i).toFixed(2)} ${yAt(value).toFixed(2)}`);
    } else if (stepped) {
      current.push(`H${xAt(i).toFixed(2)}`, `V${yAt(value).toFixed(2)}`);
    } else {
      current.push(`L${xAt(i).toFixed(2)} ${yAt(value).toFixed(2)}`);
    }
    lastIndex = i;
  });
  flush();

  return paths;
}

export interface ChartCursor {
  /** Position along the x-axis in [0, 1], in the same space as the bands. */
  fraction: number;
  /** Short label for the chip above the rule, e.g. `NOW`. */
  label?: string;
}

interface PlanTimeSeriesChartProps {
  series: PlanChartSeries[];
  unit: string;
  /** Accent colour (CSS colour or var()) for the recommended/primary curve. */
  accent: string;
  bands?: PhaseBand[];
  hourTicks?: { index: number; label: string }[];
  /** Clock label for a hovered sample; without it the tooltip shows no time. */
  hoverLabelAt?: (_index: number) => string;
  /** Describes the chart for screen readers, which can't read the curves. */
  ariaLabel: string;
  /** Baseline/Recommended key, shown top-right for comparison charts. */
  legend?: React.ReactNode;
  /** Per-variant stroke overrides; `live` defaults to the OK/green token. */
  colors?: Partial<Record<SeriesVariant, string>>;
  /** Persistent vertical rule, distinct from the transient hover cursor. */
  cursor?: ChartCursor;
  /** Pins the y-axis to this range instead of zooming to the data. */
  yRange?: { min: number; max: number };
}

function PlanTimeSeriesChart({
  series,
  unit,
  accent,
  bands = [],
  hourTicks = [],
  hoverLabelAt,
  ariaLabel,
  legend,
  colors,
  cursor,
  yRange,
}: PlanTimeSeriesChartProps) {
  const strokeFor = (variant: SeriesVariant): string | undefined => {
    const override = colors?.[variant] ?? DEFAULT_COLORS[variant];
    if (override) return override;
    // Baseline draws from CSS (muted dash); everything else takes the accent.
    return variant === 'baseline' ? undefined : accent;
  };

  // Primitives, so a fresh `yRange` object each render doesn't redo the geometry.
  const yMin = yRange?.min;
  const yMax = yRange?.max;

  const geometry = useMemo(() => {
    const drawable = series.filter(s => s.values.some(isDrawable));
    if (drawable.length === 0) return null;

    const allValues = drawable.flatMap(s => s.values).filter(isDrawable);
    if (allValues.length === 0) return null;

    const scale =
      yMin !== undefined && yMax !== undefined
        ? niceScale(yMin, yMax)
        : niceScale(Math.min(...allValues), Math.max(...allValues));
    const span = scale.max - scale.min || 1;
    const yAt = (value: number) => ((scale.max - value) / span) * 100;

    const longest = Math.max(...drawable.map(s => s.values.length));

    // The plot is a 0..100 square: samples span its width, values its height
    // (0 at the top). Bands and the cursor, already axis fractions, map across.
    const xAt = (index: number) => (longest <= 1 ? 0 : (index / longest) * 100);

    const paths = drawable.map(s => ({
      variant: s.variant,
      label: s.label,
      segments: subpaths(s.values, xAt, yAt, s.interpolation),
    }));

    const yTicks = scale.ticks.map(value => ({
      pct: yAt(value),
      label: value.toFixed(scale.decimals),
    }));

    const hoverSeries = drawable.map(s => ({
      label: s.label,
      values: s.values,
      yAt,
      color:
        colors?.[s.variant] ??
        DEFAULT_COLORS[s.variant] ??
        (s.variant === 'baseline' ? BASELINE_HOVER_COLOR : accent),
    }));

    return { paths, yTicks, hoverSeries, longest, xAt, decimals: scale.decimals };
  }, [series, accent, colors, yMin, yMax]);

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (!geometry) return null;

  const verticals = Array.from(
    { length: X_GRID_DIVISIONS - 1 },
    (_, i) => ((i + 1) / X_GRID_DIVISIONS) * 100
  );

  const hover =
    hoverIndex == null
      ? null
      : {
          xPct: geometry.xAt(hoverIndex),
          time: hoverLabelAt?.(hoverIndex),
          points: geometry.hoverSeries
            .filter(s => isDrawable(s.values[hoverIndex]))
            .map(s => ({
              label: s.label,
              color: s.color,
              yPct: s.yAt(s.values[hoverIndex] as number),
              text: `${(s.values[hoverIndex] as number).toFixed(geometry.decimals)}${
                unit ? ` ${unit}` : ''
              }`,
            })),
        };

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const frac = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(frac * geometry.longest);
    setHoverIndex(Math.max(0, Math.min(geometry.longest - 1, idx)));
  };

  const cursorPct =
    cursor && cursor.fraction >= 0 && cursor.fraction <= 1 ? cursor.fraction * 100 : null;

  return (
    <div className="pd-chart">
      <div className="pd-chart-yaxis">
        <div className="pd-chart-unit">{unit}</div>
        <div className="pd-chart-yscale">
          {geometry.yTicks.map((tick, i) => (
            <span key={i} className="pd-chart-ytick" style={{ top: `${tick.pct}%` }}>
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <div className="pd-chart-main">
        <div className="pd-chart-legendrow">{legend}</div>
        <div
          className="pd-chart-plot"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          <svg
            className="pd-chart-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-label={ariaLabel}
          >
            {geometry.yTicks.map((tick, i) => (
              <line
                key={`h-${i}`}
                x1="0"
                y1={tick.pct}
                x2="100"
                y2={tick.pct}
                className="pd-chart-grid"
              />
            ))}
            {verticals.map(x => (
              <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="100" className="pd-chart-grid" />
            ))}
            {bands.map((band, i) => {
              const x = band.start * 100;
              const width = (band.end - band.start) * 100;
              if (width <= 0) return null;
              return (
                <rect
                  key={`${band.name}-${i}`}
                  x={x}
                  y="0"
                  width={width}
                  height="100"
                  fill={phaseBandColor(i)}
                  opacity={0.2}
                />
              );
            })}
            {geometry.paths.map(path =>
              path.segments.map((d, i) => (
                <path
                  key={`${path.label}-${i}`}
                  d={d}
                  fill="none"
                  className={`pd-chart-line pd-chart-line-${path.variant}`}
                  stroke={strokeFor(path.variant)}
                />
              ))
            )}
          </svg>
          {cursorPct !== null && (
            <>
              <div className="pd-chart-nowrule" style={{ left: `${cursorPct}%` }} aria-hidden />
              {cursor?.label && (
                <div className="pd-chart-nowchip" style={{ left: `${cursorPct}%` }} aria-hidden>
                  {cursor.label}
                </div>
              )}
            </>
          )}
          {hover && (
            <>
              <div className="pd-chart-cursor" style={{ left: `${hover.xPct}%` }} aria-hidden />
              {hover.points.map(point => (
                <div
                  key={point.label}
                  className="pd-chart-dot"
                  style={{
                    left: `${hover.xPct}%`,
                    top: `${point.yPct}%`,
                    borderColor: point.color,
                  }}
                  aria-hidden
                />
              ))}
              {hover.points.length > 0 && (
                <div
                  className={`pd-chart-tip${hover.xPct > 60 ? ' pd-chart-tip-left' : ''}`}
                  style={{ left: `${hover.xPct}%` }}
                  role="status"
                >
                  {hover.time && <span className="pd-chart-tip-time">{hover.time}</span>}
                  {hover.points.map(point => (
                    <span key={point.label} className="pd-chart-tip-row">
                      <span
                        className="pd-chart-tip-swatch"
                        style={{ background: point.color }}
                        aria-hidden
                      />
                      <span className="pd-chart-tip-label">{point.label}</span>
                      <span className="pd-chart-tip-value">{point.text}</span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="pd-chart-xaxis">
          {hourTicks.map((tick, i) => (
            <span key={i}>{tick.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Memoized: the geometry pass walks every sample of every series, and the Live
 * view mounts seven of these under a clock that ticks faster than the data.
 */
export default React.memo(PlanTimeSeriesChart);
